import { Modal, Checkbox, Select, Textfield } from '@workboard/wobo-ui';
import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  useActionItem,
  ActionItemOperation as Operation,
  RelationshipType,
} from '@workboard/action-item';
import { getStateComponentIcon } from '../../../board/utils';
import * as testIds from './testIds';
import { useCopyAIForm, FormData } from './useCopyAIForm';
import { AILink } from '../ai/link';
import { StatusField } from '../state/field';
import { PriorityField } from '../priority/field';
import { EffortField } from '../effort/field';
import { ConfidenceField } from '../confidence/field';
import { WorkstreamAutocompleteField } from '../workstream/autocompleteField';
import { UserAutocompleteField } from '../user/autocompleteField';
import { CopyAiDialogActions } from './dialogActions';
import {
  FieldSet,
  InlineAutocompleteField,
  InlineSelectField,
  InlineDateField,
  ItemContainer,
  SectionHeader,
} from '../form/InlineField';
import { Loading } from '../Loading';
import { useFeatureFlag } from '../utils/getFeatureFlag';
import { DatePicker } from '../WoboUI';
import { pickerDateToUtcUnixSeconds } from '../utils/actionItemUtcDates';
import { InLineStateField } from './inLineStateField';

const RelationshipForm = styled.form`
  position: relative;
`;

const Wrapper = styled.div`
  max-width: 800px;
`;

const InlineLink = styled.div`
  height: '40px';
  display: 'flex';
  align-items: 'center';
`;

interface IProps {
  id: string;
  isProcessing?: boolean;
  onClose?(): void;
  initialData?: Partial<FormData>;
  onSubmit(data: FormData): Promise<void>;
  types: RelationshipType[];
}

export const AICopyDialog: FC<IProps> = ({
  id,
  initialData,
  isProcessing,
  onClose,
  onSubmit,
  types,
}) => {
  const { t } = useTranslation();
  const { formData, formErrors, getFieldProps, valueSetter } = useCopyAIForm(
    initialData,
    isProcessing
  );

  const closeCallback = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const submitCallback = useCallback(() => {
    onSubmit(formData as FormData);
  }, [onSubmit, formData]);

  const typeOptions = useMemo(
    () => [
      {
        descriptor: undefined,
        label: t('no relationship'),
      },
      ...types,
    ],
    [types]
  );

  const { isPermitted, actionItem } = useActionItem(id);
  const canAddRelationship = isPermitted(Operation.addRelationship);

  const enableRelationships = useFeatureFlag('pmAIRelationshipsEnabled');

  const ignoreHandler = (e: React.MouseEvent<HTMLFormElement>) =>
    e.preventDefault();

  const FooterTemplate = useCallback(
    (props) => (
      <CopyAiDialogActions {...props}>
        <Checkbox
          {...getFieldProps('createNew', (x: boolean) => x)}
          isChecked={formData.createNew}
          data-testid={testIds.createNewField}
          label={t('Create another copy')}
          value="1"
        />
      </CopyAiDialogActions>
    ),
    [getFieldProps, formData.createNew]
  );

  const { label, attributes } = actionItem?.typeAttributes || {};
  const title = `Copy ${label}`;
  const { state, rag, priority, effort } = attributes;
  const stateOptions = state?.values ?? [];
  const ragOptions = rag?.values ?? [];
  const priorityOptions = priority?.values ?? [];
  const effortOptions = effort?.values ?? [];

  return (
    <RelationshipForm
      data-testid={testIds.form}
      onReset={ignoreHandler}
      onSubmit={ignoreHandler}
    >
      <Modal
        disableSubmit={formErrors || isProcessing}
        dismissLabel={t('Cancel')}
        enableScrollContent
        isOpen
        onDismiss={closeCallback}
        onSubmit={submitCallback}
        required={false}
        submitLabel={t('Create')}
        title={title}
        actions={FooterTemplate}
      >
        {isProcessing ? <Loading /> : null}

        <Wrapper>
          <FieldSet>
            <SectionHeader>{t("Details")}</SectionHeader>
            <ItemContainer>
              <Textfield
                {...getFieldProps('title')}
                data-testid={testIds.nameField}
                label={t('Name')}
                value={formData.title}
              />
            </ItemContainer>
            <ItemContainer>
              <InlineAutocompleteField data-testid={testIds.workstreamField}>
                <WorkstreamAutocompleteField
                  aiId={id}
                  {...getFieldProps('workstreamId', (obj) => obj?.id)}
                  value={formData.workstreamId}
                  initialData={{
                    id: formData.workstreamId,
                    name: formData.workstreamName,
                    label: formData.workstreamName,
                    teamName: '',
                  }}
                />
              </InlineAutocompleteField>
            </ItemContainer>
            <ItemContainer>
              <InlineAutocompleteField data-testid={testIds.assigneeField}>
                <UserAutocompleteField
                  {...getFieldProps('assigneeId', (obj) => obj?.id)}
                  label={t('Assign To')}
                  initialData={{
                    id: initialData.assigneeId,
                    pic: initialData.assigneePic,
                    label: initialData.assigneeFullName,
                    value: initialData.assigneeFullName,
                  }}
                  value={formData.assigneeId}
                />
              </InlineAutocompleteField>
            </ItemContainer>
            <ItemContainer items={2}>
              <div>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="fromDateTimestamp">{t('Start')}</label>
                <InlineDateField data-testid={testIds.fromDateField}>
                  <DatePicker
                    {...getFieldProps('fromDateTimestamp', (date) =>
                      date ? pickerDateToUtcUnixSeconds(date) : undefined
                    )}
                    start={formData.fromDateTimestamp}
                    placeholder={t('Start Date')}
                    readOnly={false}
                  />
                </InlineDateField>
              </div>
              <div>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="dueDateTimestamp">{t('End')}</label>
                <InlineDateField data-testid={testIds.dueDateField}>
                  <DatePicker
                    {...getFieldProps('dueDateTimestamp', (date) =>
                      date ? pickerDateToUtcUnixSeconds(date) : undefined
                    )}
                    start={formData.dueDateTimestamp}
                    anchorDirection="right"
                    placeholder={t('End Date')}
                    readOnly={false}
                  />
                </InlineDateField>
              </div>
            </ItemContainer>
          </FieldSet>

          <FieldSet>
            <SectionHeader>{t("Status")}</SectionHeader>
            <ItemContainer items={2}>
              <InlineSelectField data-testid={testIds.statusField}>
                <InLineStateField
                  {...getFieldProps('stateId')}
                  value={formData.stateId}
                  label={state?.label ?? 'Status'}
                  options={stateOptions}
                />
              </InlineSelectField>
              <InlineSelectField data-testid={testIds.priorityField}>
                <InLineStateField
                  {...getFieldProps('priority')}
                  value={formData.priority}
                  label={priority?.label ?? 'Priority'}
                  options={priorityOptions}
                />
              </InlineSelectField>
            </ItemContainer>
            <ItemContainer items={2}>
              <InlineSelectField data-testid={testIds.effortField}>
                <InLineStateField
                  {...getFieldProps('effort')}
                  value={formData.effort}
                  label={effort?.label ?? 'Level of Effort'}
                  options={effortOptions}
                />
              </InlineSelectField>
              <InlineSelectField data-testid={testIds.confidenceField}>
                <InLineStateField
                  {...getFieldProps('confidence')}
                  value={formData.confidence}
                  label={rag?.label ?? 'Confidence'}
                  options={ragOptions}
                />
              </InlineSelectField>
            </ItemContainer>
          </FieldSet>

          {enableRelationships && canAddRelationship ? (
            <FieldSet>
              <SectionHeader>{t("Relationship")}</SectionHeader>
              <ItemContainer alignItems="center" items={2}>
                <InlineSelectField data-testid={testIds.relationshipTypeField}>
                  <Select
                    {...(getFieldProps('relationshipType'),
                      (o) => o?.descriptor)}
                    getOptionLabel={(o) => o.label}
                    options={typeOptions}
                    value={typeOptions.find(
                      (o) => o.descriptor === formData.relationshipType
                    )}
                    onChange={(_e, obj) =>
                      valueSetter(`relationshipType`, obj?.descriptor)
                    }
                  />
                </InlineSelectField>
                <InlineLink>
                  <AILink id={parseInt(id)} title={initialData.title} />
                </InlineLink>
              </ItemContainer>
            </FieldSet>
          ) : null}
        </Wrapper>
      </Modal>
    </RelationshipForm>
  );
};
